import { useEffect, useMemo, useState } from 'react';
import { planCarts, type CartPlan } from '@shared/routing.ts';
import { useAppState } from '../data/store.ts';

/** Current time, refreshed every `ms` (for countdowns). */
export function useNow(ms = 30_000): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

/** The live cart plan for the current list. */
export function useCartPlan(): CartPlan {
  const list = useAppState((s) => s.list);
  const staples = useAppState((s) => s.staples);
  const products = useAppState((s) => s.products);
  const stores = useAppState((s) => s.stores);
  return useMemo(() => planCarts({ items: list, staples, products, stores }), [list, staples, products, stores]);
}

/** When carts will be prepared: quiet window after the latest edit to a needed item. */
export function usePrepTiming(now: Date): { lastEdit?: Date; dueAt?: Date; minutesLeft: number; due: boolean } {
  const list = useAppState((s) => s.list);
  const quiet = useAppState((s) => s.settings.quietMinutes);
  return useMemo(() => {
    const needed = list.filter((i) => i.status === 'needed');
    if (needed.length === 0) return { minutesLeft: 0, due: false };
    const last = new Date(Math.max(...needed.map((i) => Date.parse(i.updatedAt) || 0)));
    const dueAt = new Date(last.getTime() + quiet * 60_000);
    const minutesLeft = Math.max(0, Math.ceil((dueAt.getTime() - now.getTime()) / 60_000));
    return { lastEdit: last, dueAt, minutesLeft, due: minutesLeft === 0 };
  }, [list, quiet, now]);
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
